/**
 * Where the camera goes, whose aeroplane it is looking at, and where that
 * aeroplane is drawn.
 *
 * Split out of `flight-scene.ts` when the Mission Recorder gave the camera a
 * second thing to follow. Before that the answer was always "the player", and a
 * dozen lines inside the frame loop were enough; now there are three interacting
 * rules — a chosen target, a mode that may not apply to it, and a mesh that must
 * be hidden or shown accordingly — and rules that interact are worth having
 * somewhere they can be stated once and tested.
 *
 * The four decision functions below are pure and import no `three`. Only the
 * two at the bottom, which apply what they decide, touch the scene graph.
 */

import * as THREE from "three/webgpu";
import type { Pose } from "@red-opus/engine";
import type { CameraMode } from "./camera-modes.ts";
import { targetIsPresent, type CameraTarget } from "./camera-target.ts";
import { cockpit3dModelled } from "./cockpit-airframe.ts";
import { eyeOffsetFor, LOOK_AHEAD, type LookAim } from "./cockpit-view.ts";
import type { EffectsSettings } from "./effects-settings.ts";
import { setGunsVisible, setPilotVisible } from "../three/enhanced-nodes.ts";
import type { AircraftView } from "./encounter-views.ts";
import { placeCamera, type CameraPlacement, type CameraRig } from "./flight-view.ts";
import type { GlanceDirection } from "./keys.ts";
import type { Cockpit3dQuad } from "./scene-cockpit-3d.ts";
import { resetFlyByZoom, type FlyByZoomState } from "./scene-flyby-zoom.ts";
import type { EncounterRunner } from "./sim-runner.ts";

/** Everything the camera rules read, gathered once per frame. */
export interface CameraSubject {
  readonly mode: CameraMode;
  readonly target: CameraTarget;
  /** The player's own interpolated pose. */
  readonly ownPose: Pose;
  readonly others: readonly AircraftView[];
  /** The player's bare `AircraftKey`, for the cockpit eye offset. */
  readonly aircraftKey: string;
  /**
   * Terrain height under the *player*, read off the sim. On a ground roll the
   * pilot's eye is barely a metre up, and this world is only flat until a
   * `GroundQuery` says otherwise.
   */
  readonly groundHeightM: number;
  /**
   * Whether 3D COCKPIT is actually drawing this aeroplane — the setting *and*
   * ENHANCED MODELS *and* an airframe that has had its dash-band review
   * (`cockpit3dDrawn`). One resolved boolean rather than three, so every rule
   * below reads the same answer and none of them can be given two of the three.
   */
  readonly cockpit3d: boolean;
  /**
   * AD-021: the pilot has gone over the side. Two things follow, and both are
   * about the *aeroplane he left* rather than about him — the canopy is its own
   * object (`scene-parachute.ts`) and needs nothing from here.
   */
  readonly baledOut: boolean;
  /**
   * Where he is under the canopy, once he has jumped — the engine's
   * `EncounterState.parachute` position, or `undefined` when nobody has.
   *
   * The camera follows **him** rather than the aeroplane from the moment he
   * leaves it. That is the whole event: the wreck flies its own AD-014 arc away
   * and down, and watching it instead would mean watching a fire the player is
   * no longer in while the thing he did happens off-screen.
   */
  readonly parachutePose: Pose | undefined;
}

/**
 * Whether the player is sitting in his **own** cockpit this frame.
 *
 * Not the same as `mode === "cockpit"`: watching an Albatros from a chosen
 * target turns a cockpit view into a chase view of it (`effectiveCameraMode`),
 * and the two rules below both mean "the camera is inside the player's
 * aeroplane" rather than "the bar says COCKPIT".
 */
function inOwnCockpit(subject: CameraSubject): boolean {
  return subject.target === null && subject.mode === "cockpit";
}

/**
 * The pose the camera should be looking at. A target that is no longer on the
 * roster — a tape scrubbed back past an aeroplane's spawn, or a wreck that has
 * left the fight — falls back to the player rather than leaving the camera
 * anchored to a hole.
 */
export function viewedPose(subject: CameraSubject): Pose {
  // AD-021. Only when the player has not chosen somebody else to watch — a
  // deliberate `OTHERS` pick still wins, exactly as it does over his own
  // aeroplane.
  if (subject.target === null && subject.parachutePose) return subject.parachutePose;
  if (subject.target === null) return subject.ownPose;
  return subject.others.find((entry) => entry.id === subject.target)?.pose ?? subject.ownPose;
}

/**
 * The mode that will actually be used. There is no sitting in another pilot's
 * cockpit — the original greyed its `OTHERS` column out until `EXTERNAL` was
 * chosen — so a cockpit view of somebody else's aeroplane becomes a chase view
 * of it rather than an error or a silent snap back to the player.
 */
export function effectiveCameraMode(subject: CameraSubject): CameraMode {
  // AD-021: there is no cockpit to sit in once he has left it, and a cockpit
  // camera pinned to the canopy's position would put the player's eye inside
  // his own harness. Same resolution the port already makes for a cockpit view
  // of somebody else's aeroplane, and for the same reason.
  if (subject.baledOut && subject.mode === "cockpit") return "chase";
  return subject.target !== null && subject.mode === "cockpit" ? "chase" : subject.mode;
}

/**
 * Whether the player's own mesh should be drawn.
 *
 * It is hidden in the cockpit because the camera is inside it, and when the
 * camera has gone to follow an Albatros it is not — so watching somebody else
 * always shows your own aeroplane, whatever mode the bar says.
 *
 * **3D COCKPIT inverts the cockpit case**, which is the whole of the feature:
 * with it drawing, being inside the aeroplane is the reason to draw it rather
 * than the reason not to.
 */
export function ownAircraftVisible(subject: CameraSubject): boolean {
  return subject.cockpit3d || !inOwnCockpit(subject);
}

/**
 * Whether the enhanced model's own pilot is drawn
 * (`three/enhanced-nodes.ts`'s `setPilotVisible`).
 *
 * Everywhere but one place, yes: he is measured art and every external view
 * wants him. The exception is the seat, because the cockpit camera is placed
 * *at his head* — `EYE_OFFSETS` is derived from the very sphere he is built out
 * of — so drawing him in 3D COCKPIT would fill the frame with the inside of his
 * own skull.
 */
export function ownPilotVisible(subject: CameraSubject): boolean {
  // AD-021: an abandoned aeroplane has an empty seat. Without this the pilot
  // would be in two places at once — still at the controls of the wreck
  // spiralling down, and under the canopy the camera is following.
  if (subject.baledOut) return false;
  return !(subject.cockpit3d && inOwnCockpit(subject));
}

/** Whether 3D COCKPIT's dashboard quad is drawn — exactly when the player is in his own 3D cockpit. */
export function dashQuadVisible(subject: CameraSubject): boolean {
  return subject.cockpit3d && inOwnCockpit(subject);
}

/**
 * Whether the enhanced model's own machine guns are drawn
 * (`three/enhanced-nodes.ts`'s `setGunsVisible`).
 *
 * **Exactly the complement of the dash quad**, and written that way rather than
 * as its own three-term condition, because the two are one decision: the quad
 * *is* the painted guns. `cockpit-airframe.ts` classifies every gun, sight and
 * muzzle-flash op as dashboard family on all 22 scouts, idle and firing, so
 * wherever that quad is drawn the painted Vickers are on it — half a metre from
 * the pilot's eye, with the modelled pair three metres out behind them. Two
 * pairs of guns in one cockpit (user, 2026-08-26).
 *
 * Note it is *not* conditioned on the glance. The quad turns with the aeroplane
 * rather than being nailed to the screen, so the painted guns occupy the same
 * solid angle as the modelled ones from every glance — including the moment
 * halfway through a head turn when both would be half in frame, which is the one
 * a `glance === "forward"` test would get wrong.
 */
export function ownGunsVisible(subject: CameraSubject): boolean {
  return !dashQuadVisible(subject);
}

/** One frame's placement, ready for `placeCamera`. */
export function cameraPlacementFor(
  subject: CameraSubject,
  dt: number,
  glance: GlanceDirection,
  aim: LookAim = LOOK_AHEAD,
): CameraPlacement {
  return {
    mode: effectiveCameraMode(subject),
    pose: viewedPose(subject),
    dt,
    glance,
    aim,
    eyeOffset: eyeOffsetFor(subject.aircraftKey),
    groundHeightM: subject.groundHeightM,
    cockpit3d: subject.cockpit3d,
  };
}

/** Copies the interpolated sim pose onto the aircraft group (same frame convention, no swizzle). */
export function applyPose(aircraft: THREE.Group, pose: Pose): void {
  aircraft.position.set(pose.position.x, pose.position.y, pose.position.z);
  aircraft.quaternion.set(pose.orientation.x, pose.orientation.y, pose.orientation.z, pose.orientation.w);
}

/**
 * The slice of the flight scene these functions need. Structural rather than
 * the scene's own state type, so this module stays independent of the renderer
 * lifecycle that owns it — and so a test can drive the camera with a stub.
 */
export interface CameraOwner {
  readonly rig: CameraRig;
  readonly aircraft: THREE.Group;
  readonly runner: EncounterRunner;
  readonly cameraMode: CameraMode;
  readonly cameraTarget: CameraTarget;
  /** A baked model path (`"models/CAMEL"`); the eye offset keys on the bare `AircraftKey`. */
  readonly playerModelId: string;
  /** Which port extensions are on — `cockpit3d` and `enhancedModels` between them decide whether the seat is a 3D one. */
  readonly effects: EffectsSettings;
  /** 3D COCKPIT's dashboard quad, or `null` on a scene that has none yet. */
  readonly dashQuad: Cockpit3dQuad | null;
}

/**
 * Whether 3D COCKPIT is drawing this aeroplane at all: the setting, ENHANCED
 * MODELS (the feature's whole premise is the `.glb` — see `EffectsSettings`),
 * and an airframe whose dash-band classification has had its review pass.
 *
 * Read in one place and carried on the subject, so the three conditions cannot
 * come apart between the mesh, the pilot and the quad.
 */
export function cockpit3dDrawn(effects: EffectsSettings, aircraftKey: string): boolean {
  return effects.cockpit3d && effects.enhancedModels && cockpit3dModelled(aircraftKey);
}

/** `playerModelId` is a baked path; `eyeOffsetFor` wants the bare key. */
export function playerAircraftKey(owner: Pick<CameraOwner, "playerModelId">): string {
  return owner.playerModelId.replace(/^models\//, "");
}

/**
 * The canopy as a `Pose` the camera can be pointed at.
 *
 * Upright, always: a parachute has no attitude worth carrying, and the identity
 * orientation is what keeps the chase boom level behind him instead of
 * inheriting the spin of the aeroplane he jumped out of.
 *
 * `undefined` once he is `dead` as well as when nobody jumped — there is no
 * descent left to watch, and the ordinary end-of-sortie path takes over.
 */
function parachutePoseOf(owner: CameraOwner): Pose | undefined {
  const chute = owner.runner.encounter?.().parachute;
  if (!chute || chute.status === "dead") return undefined;
  return { position: chute.position, orientation: { x: 0, y: 0, z: 0, w: 1 } };
}

export function cameraSubjectOf(owner: CameraOwner, pose: Pose): CameraSubject {
  const aircraftKey = playerAircraftKey(owner);
  return {
    mode: owner.cameraMode,
    target: owner.cameraTarget,
    ownPose: pose,
    others: owner.runner.others(),
    aircraftKey,
    cockpit3d: cockpit3dDrawn(owner.effects, aircraftKey),
    // Read off the sim rather than assumed to be datum: on a ground roll the
    // pilot's eye is barely a metre up, and this world is only flat until a
    // `GroundQuery` says otherwise.
    groundHeightM: owner.runner.state().readout.groundHeightM,
    // AD-021, read off the sim like the ground under him: the pilot's own
    // `SimState` is where "the seat is empty" lives (`flight/physiology.ts`).
    baledOut: owner.runner.state().pilot.bailedOut,
    parachutePose: parachutePoseOf(owner),
  };
}

/** Re-pins the fly-by camera on whatever is now being watched. */
export function reanchorFlyBy(owner: CameraOwner): void {
  owner.rig.flyByAnchorPose = viewedPose(cameraSubjectOf(owner, owner.runner.pose()));
}

/** The write half of `CameraOwner`: what the scene API's two camera setters mutate. */
export interface CameraChooser extends CameraOwner, FlyByZoomState {
  cameraMode: CameraMode;
  cameraTarget: CameraTarget;
}

/**
 * The scene API's `setCameraMode`, here beside the re-anchoring it triggers.
 * Leaving the camera the wheel was zooming: the least-surprising reset
 * (`scene-flyby-zoom.ts`) rather than carrying a zoom level forward with no
 * on-screen readout of what it was left at.
 */
export function chooseCameraMode(owner: CameraChooser, mode: CameraMode): void {
  if (owner.cameraMode === "flyBy" && mode !== "flyBy") resetFlyByZoom(owner);
  owner.cameraMode = mode;
  if (mode === "flyBy") reanchorFlyBy(owner);
}

/**
 * The scene API's `setCameraTarget`, checked against the live roster rather
 * than stored blind: a scrub can move an aeroplane out from under a chosen
 * target, and a camera anchored to an id that is not in the sky would sit
 * still while the fight carried on off screen.
 */
export function chooseCameraTarget(owner: CameraChooser, target: CameraTarget): void {
  owner.cameraTarget = targetIsPresent(target, owner.runner.others().map((entry) => entry.id)) ? target : null;
  if (owner.cameraMode === "flyBy") reanchorFlyBy(owner);
}

/**
 * Places the camera and decides what of the player's own aeroplane is drawn.
 *
 * Without 3D COCKPIT the mesh is hidden in the cockpit, because the camera sits
 * inside a low-poly model with no modelled interior and the correct "what would
 * be visible" answer at that fidelity is nothing — the same call the original's
 * own DOS cockpit made (a flat baked dashboard, not a 3D interior). With it, the
 * four visibility rules above replace that judgement for the airframes whose
 * enhanced `.glb` carries an interior, a pilot and a pair of guns on their own
 * nodes, and a cockpit opening cut into the decking — which since phase 2 is all
 * 22 flyable scouts.
 *
 * All four are re-asserted every frame rather than on edges. They are property
 * writes on at most four nodes of a group that holds one rung, and an edge
 * latch would have to be invalidated by the two things that can replace the
 * group underneath it — a mid-flight ENHANCED MODELS swap, and the paint reload
 * behind it — which is a cache with two invalidation paths in exchange for
 * nothing measurable.
 */
/**
 * Returns the subject it decided on, so the rest of the frame can ask the same
 * questions of the same answer. The interior treatment
 * (`scene-cockpit-interior.ts`) needs `dashQuadVisible`, and recomputing the
 * subject for it would mean a second `runner.others()` walk and — worse — a
 * second chance for the two to disagree about which cockpit the player is in.
 *
 * Note the order: all four visibility rules are decided from `subject` — where
 * the player is sitting and in which cockpit — and **none of them from `glance`
 * or `aim`**, which reach only `cameraPlacementFor` on the last line. Where the
 * pilot has turned his head is a question about the *camera*, not about which of
 * his aeroplane's parts exist; a free-look drag that made the modelled guns
 * appear and disappear as it swung would be the same bug the glance-independence
 * argument on `ownGunsVisible` rules out, arriving by the other door.
 */
export function updateCamera(
  owner: CameraOwner,
  pose: Pose,
  dt: number,
  glance: GlanceDirection,
  aim: LookAim = LOOK_AHEAD,
): CameraSubject {
  const subject = cameraSubjectOf(owner, pose);
  owner.aircraft.visible = ownAircraftVisible(subject);
  setPilotVisible(owner.aircraft, ownPilotVisible(subject));
  setGunsVisible(owner.aircraft, ownGunsVisible(subject));
  if (owner.dashQuad) owner.dashQuad.setVisible(dashQuadVisible(subject));
  placeCamera(owner.rig, cameraPlacementFor(subject, dt, glance, aim));
  return subject;
}
